import { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import './ServiceDashboard.css';
import { Service } from '../../../../data_types';
import { fetchServices } from '../../../backend/services';

const ServiceDashboard = () => {
    const [newServiceTitle, setNewServiceTitle] = useState('');
    const [newServiceDescription, setNewServiceDescription] = useState('');
    const [newServiceImage, setNewServiceImage] = useState<File | null>(null);
    const [newServiceVideo, setNewServiceVideo] = useState<File | null>(null);
    const [deleteServiceId, setDeleteServiceId] = useState('');
    const [servicesPerRow, setServicesPerRow] = useState('');
    const [hasVideoHover, setHasVideoHover] = useState(false);

    const [serviceData, setServiceData] = useState<{ services: Service[] }>({ services: [] });

    useEffect(() => {
        const loadServices = async () => {
            const fetchedData = await fetchServices();
            setServiceData({ services: fetchedData.services });
        };
        loadServices();
    }, []);


    const servicesBaseUrl = process.env.REACT_APP_SERVICES_BASE_URL; // Base URL for the API server

    const handleAddServiceSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Basic validation for required fields
        if (!newServiceTitle || !newServiceImage || !newServiceDescription) {
            alert('Please provide all required fields: title, description, and image.');
            return;
        }

        // Conditional validation for video hover
        if (hasVideoHover && !newServiceVideo) {
            alert('Please provide a video file or uncheck "Enable Video on Hover".');
            return;
        }

        const formData = new FormData();
        formData.append('title', newServiceTitle);
        formData.append('description', newServiceDescription);
        formData.append('image', newServiceImage);
        formData.append('hasVideoHover', hasVideoHover.toString());

        if (hasVideoHover && newServiceVideo) {
            formData.append('video', newServiceVideo);
        }

        try {
            const response = await fetch(`${servicesBaseUrl}`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            if (response.ok) {
                alert('Service added successfully');
                setNewServiceTitle('');
                setNewServiceDescription('');
                setNewServiceImage(null);
                setNewServiceVideo(null);
                setHasVideoHover(false);
            } else {
                alert('Failed to add service');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error adding service');
        }
    };

    const handleDeleteServiceSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!deleteServiceId) {
            alert('Please provide the service ID to delete.');
            return;
        }

        try {
            const response = await fetch(`${servicesBaseUrl}/${deleteServiceId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                alert('Service deleted successfully');
                setDeleteServiceId('');
            } else {
                alert('Failed to delete service');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting service');
        }
    };

    const handleUpdateServicesPerRow = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!servicesPerRow || isNaN(Number(servicesPerRow))) {
            alert('Please provide a valid number for services per row.');
            return;
        }

        try {
            const response = await fetch(`${servicesBaseUrl}/updateServicesPerRow`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ servicesPerRow: servicesPerRow }),
                credentials: 'include',
            });

            if (response.ok) {
                alert('servicesPerRow updated successfully');
                setServicesPerRow('');
            } else {
                alert('Failed to update servicesPerRow');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error updating servicesPerRow');
        }
    };

    return (
        <Box className="service-dashboard">
            <form onSubmit={handleAddServiceSubmit} encType='multipart/form-data'>
                <h2>Add a New Service</h2>
                <TextField
                    label="Service Title"
                    value={newServiceTitle}
                    onChange={(e) => setNewServiceTitle(e.target.value)}
                    fullWidth
                    required
                />
                <TextField
                    label="Service Description"
                    value={newServiceDescription}
                    onChange={(e) => setNewServiceDescription(e.target.value)}
                    fullWidth
                    required
                />
                <label htmlFor="serviceImage">Service Image:</label>
                <input
                    title='Service Image'
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewServiceImage(e.target.files?.[0] || null)}
                    required
                />
                <label htmlFor="serviceVideo">Service Video:</label>
                <input
                    title='Service Video'
                    type="file"
                    accept="video/*"
                    onChange={(e) => setNewServiceVideo(e.target.files?.[0] || null)}
                    disabled={!hasVideoHover}
                />
                <label>
                    <input
                        type="checkbox"
                        checked={hasVideoHover}
                        onChange={() => setHasVideoHover(!hasVideoHover)}
                    />
                    Enable Video on Hover
                </label>

                <Button type="submit" variant="contained" color="primary">
                    Add Service
                </Button>
            </form>

            <form onSubmit={handleDeleteServiceSubmit}>
                <h2>Delete a Service</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={deleteServiceId}
                        onChange={(e) => setDeleteServiceId(e.target.value)}
                    >
                        {serviceData?.services?.length > 0 ? (
                            serviceData?.services.map((service) => (
                                <MenuItem value={service.displayId}>{service.serviceTitle}</MenuItem>
                            ))
                        ) : (
                            <p>No services available</p>
                        )}
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" color="secondary">
                    Delete Service
                </Button>
            </form>

            <form onSubmit={handleUpdateServicesPerRow}>
                <h2>Update Services Per Row</h2>
                <TextField
                    label="Services Per Row"
                    value={servicesPerRow}
                    onChange={(e) => setServicesPerRow(e.target.value)}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Update Services Per Row
                </Button>
            </form>
        </Box>
    );
};

export default ServiceDashboard;
